<template>
    <Barrier id="edit-board-module" v-if="visible" @click="cancel">
        <Loader v-if="loading" :loading="true"></Loader>
        <Container v-else :fill="true">
            <EditModuleHeader :module="moduleDetails" @save="validate" @cancel="cancel"></EditModuleHeader>
            <ProductsCatalog id="products-catalog" :products="relatedProducts"></ProductsCatalog>
            <section ref="container">
                <ModulePreview
                    v-for="(element, i) in editedElements"
                    :key="i"
                    :index="i"
                    :element="element"
                    :shadeByGroup="shadeByGroup[i]"
                    :highlightTester="highlightTesters[i]"
                    :groups="groups"
                    :shadesPerProduct="shadesPerProduct"
                    class="preview"
                    :style="elementsPosition[i]"
                    @mouseup="e => applyShadeTo(e, i)"
                    @openMenu="openMenu"
                    @closeMenu="closeMenu"
                    @checkMenu="checkMenu"
                    :size="windowResizeCount"
                ></ModulePreview>
            </section>
            <ShadesCatalog id="shades-catalog" @selectShade="selectShade" :shadeSelected="shadeSelected" :shades="shadesPerProduct"></ShadesCatalog>
            <div class="menu" ref="menu" v-show="menu">
                <p>Higlights:</p>
                <li
                    @click="applyHighlightTesterListener(color);"
                    v-for="(color, index) in colors"
                    :key="index"
                    :style="{ backgroundColor: color.code }"
                ></li>
            </div>
        </Container>
    </Barrier>
</template>

<script>
import Barrier from "@/plano/components/modal-barrier";
import Container from "@/plano/components/modal-container";
import ShadesCatalog from "./shades-catalog";
import ProductsCatalog from "./products-catalog";
import EditModuleHeader from "./edit-module-header";
import componentService from "@/services/component";
import productService from "@/services/product";
import shadesService from "@/services/shades";
import Loader from "@/components/loader/loader";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import { Context } from "plano_engine";
import ModulePreview from "./module-preview";
import { mapGetters } from "vuex";
import { NONE_HIGHLIGHT } from "@/plano/elements";

let editBoardModuleWidget;
window.editBoardModule = function editBoardModule(data, onValidate, onCancel) {
    console.assert(data.element instanceof BoardModuleElement);
    const url = data.element.imageURL;
    const editedElements = Context.selection.selection.filter(item => item !== data.element && item.modelId() === data.element.modelId());

    Object.assign(editBoardModuleWidget, {
        data,
        editedElements: [data.element, ...editedElements],
        onValidate,
        onCancel,
        visible: true
    });
};

function isShadeSlot(item) {
    const id = item.getAttribute && item.getAttribute("id");
    return id && id.startsWith("path_");
}

function findShadeSlotId(/** @type {MouseEvent} */ e, data) {
    /** @type {Element} */
    const pathElement = e.composedPath().find(isShadeSlot);
    console.assert(pathElement);

    return pathElement.getAttribute("id");
}

function findGroupWithSlotId(slotId, groups) {
    return groups.find(group => {
        return group.modules.includes(slotId);
    });
}

function computeElementsPosition(editedElements, containerSize) {
    function getGlobalPosition(element) {
        if (element instanceof BoardPaper) return;
        let parentPos;
        if (element.parent()) parentPos = getGlobalPosition(element.parent());

        const res = {
            x: element.posX - element.width / 2,
            y: element.posY - element.height / 2,
            width: element.width,
            height: element.height
        };
        if (parentPos) {
            res.x -= parentPos.x;
            res.y -= parentPos.y;
        }

        return res;
    }

    let minX = 999999;
    let minY = 999999;
    let maxX = 0;
    let maxY = 0;

    for (const element of editedElements) {
        const pos = getGlobalPosition(element);

        const startX = pos.x;
        const endX = startX + pos.width;

        if (startX < minX) minX = startX;
        if (endX > maxX) maxX = endX;

        const startY = pos.y;
        const endY = startY + pos.height;

        if (startY < minY) minY = startY;
        if (endY > maxY) maxY = endY;
    }

    const res = [];

    const previewWidth = maxX - minX;
    const previewHeight = maxY - minY;

    const previewRatio = previewWidth / previewHeight;
    const containerRatio = containerSize.width / containerSize.height;

    let leftPadding = 0;
    let topPadding = 0;

    const fullScalledWidth = (containerSize.width / containerRatio) * previewRatio;
    const fullScalledHeight = (containerSize.height * containerRatio) / previewRatio;

    let widthRatio = 1;
    let heightRatio = 1;

    if (containerRatio > previewRatio) {
        widthRatio = (1 / (maxX - minX)) * fullScalledWidth;
        heightRatio = (1 / (maxY - minY)) * containerSize.height;
        leftPadding = (containerSize.width - fullScalledWidth) / 2;
    }
    if (previewRatio > containerRatio) {
        widthRatio = (1 / (maxX - minX)) * containerSize.width;
        heightRatio = (1 / (maxY - minY)) * fullScalledHeight;
        topPadding = (containerSize.height - fullScalledHeight) / 2;
    }

    for (const element of editedElements) {
        const pos = getGlobalPosition(element);

        const translatedPos = {
            top: pos.y - minY,
            left: pos.x - minX,
            width: element.width,
            height: element.height
        };

        res.push({
            top: `${translatedPos.top * heightRatio + topPadding}px`,
            left: `${translatedPos.left * widthRatio + leftPadding}px`,
            width: `${translatedPos.width * widthRatio}px`,
            height: `${translatedPos.height * heightRatio}px`
        });
    }
    return res;
}

export default {
    components: { ModulePreview, Barrier, Container, ShadesCatalog, ProductsCatalog, EditModuleHeader, Loader },
    mixins: [TryLoadMixin],
    data: () => ({
        data: undefined,
        editedElements: [],
        elementsPosition: [],
        svg: undefined,
        onValidate: undefined,
        onCancel: undefined,
        visible: false,
        moduleDetails: undefined,
        /** @type {Array<{ productId: number, productName: Translation, shades: Shade[] }>} */
        shadesPerProduct: [],
        /** @type {Array<{ [groupId: string]: { id: number, properties: any }}>} */
        shadeByGroup: [],
        /** @type {Array<{ [tester: string]: colorCode }>}*/
        highlightTesters: [],
        relatedProducts: [],
        shadeSelected: undefined,
        // since window size can't be used as a computed, we use an integer
        // that changes whenever the window.resize event is emitted, to recompute computed variables
        windowResizeCount: 0,
        testerNode: undefined,
        menu: false
    }),
    computed: {
        ...mapGetters("currentUser", ["currentBrand"]),
        colors() {
            const colorsBand = this.currentBrand && this.currentBrand.brandHightlighColorByBrand;
            colorsBand.push(NONE_HIGHLIGHT);
            return colorsBand;
        },
        moduleId() {
            return this.data && this.data.element.modelId();
        },
        groups() {
            return this.moduleDetails && this.moduleDetails.config.groups;
        },
        isMultiEdit() {
            return this.editedElements.length > 0;
        },
        containerSize() {
            // subscribe the computed to the window size
            this.windowResizeCount;
            if (this.visible && !this.loading) {
                /** @type {Element} */
                const container = this.$refs.container;
                if (!container) {
                    // eslint-disable-next-line vue/no-async-in-computed-properties
                    setTimeout(() => this.resizeListener(), 0);
                    return { width: 0, height: 0 };
                } else {
                    return { width: container.clientWidth, height: container.clientHeight };
                }
            } else {
                return { width: 0, height: 0 };
            }
        }
    },
    watch: {
        editedElements() {
            this.shadeByGroup = this.editedElements.map(element => element.shadeByGroup);
            this.highlightTesters = this.editedElements.map(element => element.highlightTester);
            this.elementsPosition = computeElementsPosition(this.editedElements, this.containerSize);
        },
        containerSize() {
            this.elementsPosition = computeElementsPosition(this.editedElements, this.containerSize);
        },
        async moduleId(moduleId) {
            // reset details to make sure the modal doesn't show the previously opened module
            this.moduleDetails = undefined;
            this.shadesPerProduct = [];
            if (!moduleId) {
                return;
            }

            this.tryLoad(async () => {
                const [moduleDetails, relatedProducts] = await Promise.all([componentService.get(moduleId), productService.fromModule(moduleId)]);
                console.assert(
                    moduleDetails && moduleDetails.config && moduleDetails.config.groups && !moduleDetails.config.groups.some(g => !g.modules),
                    "Module badly formatted"
                );
                this.moduleDetails = moduleDetails;
                this.relatedProducts = relatedProducts.data;

                this.shadesPerProduct = await Promise.all(
                    this.relatedProducts.map(async product => {
                        const shades = (await shadesService.fromProduct(product.id)).data;
                        return { productId: product.id, productName: product.terms.name, shades };
                    })
                );
            });
        }
    },
    created() {
        editBoardModuleWidget = this;
        window.addEventListener("resize", this.resizeListener);
    },
    methods: {
        openMenu({ x, y, textNode, index }) {
            this.menu = true;
            this.testerNode = { textNode, index };

            let menu = this.$refs.menu;
            menu.style.left = `${x}px`;
            menu.style.top = `${y}px`;
            menu.style.display = "block";
        },
        closeMenu() {
            this.testerNode = undefined;
            this.menu = false;

            let menu = this.$refs.menu;
            menu.style.left = `0`;
            menu.style.top = `0`;
            menu.style.display = "none";
        },
        checkMenu() {
            if (this.menu) this.closeMenu();
        },
        applyShadeTo(e, index) {
            const element = this.editedElements[index];

            if (isShadeSlot(e.target) && this.shadeSelected) {
                const slotId = findShadeSlotId(e, this.moduleDetails);
                const group = findGroupWithSlotId(slotId, this.groups);
                if (!group) return;

                const newShades = [...this.shadeByGroup];
                newShades.splice(index, 1, {
                    ...this.shadeByGroup[index],
                    [group.rowId]: this.shadeSelected.id
                });
                this.shadeByGroup = newShades;
            }
        },
        /**
         * We add tester propertie and the event Object
         * @param color {id: Number, code: String, label: String}
         */
        applyHighlightTesterListener(color) {
            if (this.testerNode) {
                const { textNode, index } = this.testerNode;
                const tester = textNode.getAttribute("tester");
                const newHighlightsTesters = [...this.highlightTesters];
                const rowId = textNode.getAttribute("rowId");
                newHighlightsTesters.splice(index, 1, { ...this.highlightTesters[index], [tester]: { color, rowId } });

                this.highlightTesters = newHighlightsTesters;
            }
        },
        resizeListener() {
            this.windowResizeCount++;
        },
        selectShade(shade) {
            this.shadeSelected = shade;
        },
        validate() {
            // throw Error("unimplemented");
            for (let i = 0; i < this.editedElements.length; i++) {
                const element = this.editedElements[i];

                element.shadeByGroup = this.shadeByGroup[i];
                element.highlightTester = this.highlightTesters[i];
                element.rasterisePreview(this.moduleDetails.config.groups, this.shadesPerProduct);
            }
            this.onValidate({
                fixed: this.fixed
            });
            this.clear();
        },
        cancel() {
            this.onCancel();
            this.clear();
        },

        clear() {
            this.moduleDetails = undefined;
            this.data = undefined;
            this.svg = undefined;
            this.onValidate = undefined;
            this.onCancel = undefined;
            this.visible = false;
            this.loading = false;
            this.moduleDetails = undefined;
            this.editedElements = [];
            this.shadeByGroup = [];
            this.shadesPerProduct = [];
            this.highlightTesters = [];
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeListener);
        if (this.onCancel) this.onCancel();
        this.clear();
    }
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css?family=Oswald:200");

#edit-board-module .modal-container {
    display: grid;
    padding: 4px;
    grid-row-gap: 4px;

    grid-template:
        "head head head" 55px
        "products preview catalog" 1fr
        / 250px 1fr 300px;

    header {
        grid-area: head;
    }

    #products-catalog {
        grid-area: products;
    }

    #shades-catalog {
        grid-area: catalog;
    }

    > section {
        grid-area: preview;
        position: relative;
        display: flex;
        align-items: stretch;
        margin: 12px;
        overflow: auto;

        > .preview {
            position: absolute;
        }
    }
}
.menu {
    border: 0.5px #aaa solid;
    position: fixed;
    background-color: #fff;
    width: 150px;
    padding: 0px 5px 5px 5px;
    li {
        list-style: none;
        display: block;
        width: 100%;
        height: 15px;
        margin: 5px 0px;
        cursor: pointer;
        border: 0.5px #000 solid;
        transition: all 0.3s ease;
        &:hover {
            opacity: 0.7;
        }
    }
}
</style>



// WEBPACK FOOTER //
// index.vue?1515b0f2